import { useState, useEffect, useCallback } from 'react';
import { get } from '../../../api/client';
import { useAuthStore } from '../../../stores/authStore';
import type { CustomerTag } from '../../../types';

export interface CustomerDetail {
  id: string;
  avatar: string | null;
  fullName: string | null;
  /** 辅助称呼（系统自动生成）：注册升格时姓名与原档案有出入时才有值，仅客户页显示 */
  displayName: string | null;
  phone: string | null;
  backupPhone: string | null;
  email: string | null;
  companyName: string | null;
  idCard: string | null;
  location: string | null;
  address: string | null;
  emergencyContact: string | null;
  emergencyPhone: string | null;
  introduction: string | null;
  /** 档案状态：激活 / 停用 / 禁用（停用可修改原因后恢复，禁用需管理员解除） */
  status: string | null;
  /** 停用/禁用原因说明 */
  stopReason: string | null;
  isUser: boolean | null;
  ucId: string;
  customerTypeId: string | null;
  /** 后端 join 出来的类型名，详情页与列表卡片唯一的名字来源（目录里没有这一行时仍然有值） */
  customerTypeName: string | null;
  tags: CustomerTag[];
  createdAt: string | null;
}

export interface CustomerType {
  id: string;
  name: string;
}

export interface AvailableTag {
  id: string;
  name: string;
  color: string | null;
}

export function useCustomerDetail(customerId: string | undefined) {
  const { profile } = useAuthStore();
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [customerTypes, setCustomerTypes] = useState<CustomerType[]>([]);
  const [availableTags, setAvailableTags] = useState<AvailableTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!customerId || !profile?.id) return;
    setLoading(true);
    setError(null);
    try {
      // 三个请求并行执行，减少加载时间
      const [customerDetail, types, tags] = await Promise.all([
        get<CustomerDetail>(`/customers/${customerId}`),
        get<CustomerType[]>('/customer-types'),
        get<AvailableTag[]>('/customer-tags'),
      ]);
      if (!customerDetail) throw new Error('获取客户详情失败');

      setDetail(customerDetail);
      setCustomerTypes(types ?? []);
      setAvailableTags(tags ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [customerId, profile?.id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return { detail, customerTypes, availableTags, loading, error, refresh: fetchDetail };
}
